import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useReactFlow } from 'reactflow';
import { TextElement } from '../types';
import { X } from 'lucide-react';

interface TextLayerProps {
  textElements: TextElement[];
  selectedTextElementId: string | null;
  onTextElementCreate: (element: TextElement) => void;
  onTextElementUpdate: (id: string, updates: Partial<TextElement>) => void;
  onTextElementDelete: (id: string) => void;
  onTextElementSelect: (id: string | null) => void;
  tool: string;
  gridSettings: { enabled: boolean; size: number; snapToGrid: boolean };
}

const TextLayer: React.FC<TextLayerProps> = ({
  textElements,
  selectedTextElementId,
  onTextElementCreate,
  onTextElementUpdate,
  onTextElementDelete,
  onTextElementSelect,
  tool,
  gridSettings
}) => {
  const { screenToFlowPosition, getViewport } = useReactFlow();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const textInputRefs = useRef<{ [key: string]: HTMLTextAreaElement | null }>({});

  const snapToGrid = useCallback((value: number) => {
    if (!gridSettings.snapToGrid) return value;
    return Math.round(value / gridSettings.size) * gridSettings.size;
  }, [gridSettings]);

  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    if (tool !== 'text') return;
    
    // Don't create text if clicking on an existing text element
    const target = e.target as HTMLElement;
    if (target.closest('.text-element')) return;

    const flowPosition = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const snappedX = snapToGrid(flowPosition.x);
    const snappedY = snapToGrid(flowPosition.y);

    const newTextElement: TextElement = {
      id: `text_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      x: snappedX,
      y: snappedY,
      text: '',
      style: {
        fontSize: 16,
        fontFamily: 'Arial',
        fontWeight: 'normal',
        fontStyle: 'normal',
        textDecoration: 'none',
        color: '#000000',
        textAlign: 'left'
      }
    };

    onTextElementCreate(newTextElement);
    setEditingId(newTextElement.id);
    // Focus will be handled by useEffect
  }, [tool, screenToFlowPosition, snapToGrid, onTextElementCreate]);

  const handleTextDoubleClick = useCallback((e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setEditingId(id);
    onTextElementSelect(id);
  }, [onTextElementSelect]);

  const handleTextClick = useCallback((e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    onTextElementSelect(id);
  }, [onTextElementSelect]);

  const handleTextChange = useCallback((id: string, text: string) => {
    onTextElementUpdate(id, { text });
  }, [onTextElementUpdate]);

  const handleTextBlur = useCallback((id: string) => {
    setEditingId(null);
    const element = textElements.find(te => te.id === id);
    if (element && !element.text.trim()) {
      // Delete empty text elements
      onTextElementDelete(id);
    }
  }, [textElements, onTextElementDelete]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      setEditingId(null);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingId(null);
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      if (e.currentTarget === document.activeElement) {
        const element = textElements.find(te => te.id === id);
        if (element && element.text === '') {
          e.preventDefault();
          onTextElementDelete(id);
          setEditingId(null);
        }
      }
    }
  }, [textElements, onTextElementDelete]);

  const handleMouseDown = useCallback((e: React.MouseEvent, id: string) => {
    if (editingId === id) return;
    
    e.stopPropagation();
    const element = textElements.find(te => te.id === id);
    if (!element) return;

    const viewport = getViewport();
    const startScreenX = e.clientX;
    const startScreenY = e.clientY;
    const startElementScreenX = element.x * viewport.zoom + viewport.x;
    const startElementScreenY = element.y * viewport.zoom + viewport.y;
    
    const offsetX = startScreenX - startElementScreenX;
    const offsetY = startScreenY - startElementScreenY;

    setDraggingId(id);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newScreenX = moveEvent.clientX - offsetX;
      const newScreenY = moveEvent.clientY - offsetY;
      const flowPosition = screenToFlowPosition({ x: newScreenX, y: newScreenY });
      const snappedX = snapToGrid(flowPosition.x);
      const snappedY = snapToGrid(flowPosition.y);
      onTextElementUpdate(id, { x: snappedX, y: snappedY });
    };

    const handleMouseUp = () => {
      setDraggingId(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [editingId, textElements, getViewport, screenToFlowPosition, snapToGrid, onTextElementUpdate]);

  // Focus input when editing starts
  useEffect(() => {
    if (editingId && textInputRefs.current[editingId]) {
      const input = textInputRefs.current[editingId];
      if (input) {
        input.focus();
        input.select();
      }
    }
  }, [editingId]);

  const viewport = getViewport();

  return (
    <div 
      className="absolute inset-0 pointer-events-none z-[40]"
      onClick={handleCanvasClick}
      style={{ pointerEvents: tool === 'text' ? 'auto' : 'none' }}
    >
      {textElements.map((element) => {
        const isSelected = selectedTextElementId === element.id;
        const isEditing = editingId === element.id;
        const isDragging = draggingId === element.id;

        // Convert flow position to screen position
        const screenX = element.x * viewport.zoom + viewport.x;
        const screenY = element.y * viewport.zoom + viewport.y;

        const textStyle: React.CSSProperties = {
          fontSize: `${element.style.fontSize}px`,
          fontFamily: element.style.fontFamily,
          fontWeight: element.style.fontWeight,
          fontStyle: element.style.fontStyle,
          textDecoration: element.style.textDecoration,
          color: element.style.color,
          textAlign: element.style.textAlign,
          lineHeight: '1.4',
          outline: 'none',
          border: 'none',
          background: 'transparent',
          resize: 'none',
          overflow: 'hidden',
          width: element.width ? `${element.width}px` : 'auto',
          height: element.height ? `${element.height}px` : 'auto',
          minWidth: '20px',
          minHeight: '20px',
          maxWidth: '500px',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          whiteSpace: 'pre-wrap',
        };

        return (
          <div
            key={element.id}
            className={`text-element absolute pointer-events-auto ${isDragging ? 'opacity-50' : ''}`}
            style={{
              left: `${screenX}px`,
              top: `${screenY}px`,
              transform: `scale(${viewport.zoom})`,
              transformOrigin: 'top left',
              zIndex: 1, // Ensure text elements are always interactive
            }}
            onDoubleClick={(e) => handleTextDoubleClick(e, element.id)}
            onClick={(e) => handleTextClick(e, element.id)}
            onMouseDown={(e) => handleMouseDown(e, element.id)}
          >
            {isEditing ? (
              <textarea
                ref={(el) => { 
                  textInputRefs.current[element.id] = el;
                  // Auto-resize textarea based on content
                  if (el) {
                    el.style.height = 'auto';
                    el.style.height = `${Math.max(el.scrollHeight, 20)}px`;
                  }
                }}
                value={element.text}
                onChange={(e) => {
                  handleTextChange(element.id, e.target.value);
                  // Auto-resize on change
                  const textarea = e.target as HTMLTextAreaElement;
                  textarea.style.height = 'auto';
                  textarea.style.height = `${Math.max(textarea.scrollHeight, 20)}px`;
                }}
                onBlur={() => handleTextBlur(element.id)}
                onKeyDown={(e) => handleKeyDown(e, element.id)}
                style={{
                  ...textStyle,
                  overflow: 'hidden',
                  resize: 'none',
                  minHeight: '20px',
                  maxWidth: '500px',
                  width: 'auto',
                }}
                className="resize-none"
                autoFocus
              />
            ) : (
              <div
                style={textStyle}
                className="whitespace-pre-wrap break-words cursor-text"
              >
                {element.text || <span className="opacity-40 italic">Click to edit</span>}
              </div>
            )}
            {isSelected && !isEditing && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onTextElementDelete(element.id);
                }}
                className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-lg z-10"
                style={{ transform: `scale(${1 / viewport.zoom})` }}
                title="Delete text"
              >
                <X size={12} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TextLayer;
